import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft, Pin, Square, Send, Share2, CheckCircle2, Upload, X, EyeOff, Eye, Copy, ListChecks,
  Sparkles, FileDown, Gauge, Tags, GripVertical, MessageSquare,
} from "lucide-react";
import { fmtRelative, readFileAsDataURL } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import PdfCanvas from "@/components/PdfCanvas";
import VideoCanvas from "@/components/VideoCanvas";
import WebsiteCanvas from "@/components/WebsiteCanvas";
import { warnTouchPrecision } from "@/lib/touchWarn";
import { useDragResize } from "@/lib/useDragResize";
import ShareDialog from "@/components/ShareDialog";
import ReviewersDialog from "@/components/ReviewersDialog";
import ActivityLogDialog from "@/components/ActivityLogDialog";
import RatingWidget from "@/components/RatingWidget";
import { useAuth } from "@/lib/auth";

import { ArrowRight, Circle as CircleIcon } from "lucide-react";

const TOOLS = [
  { id: "pin", icon: Pin, label: "Pin" },
  { id: "rect", icon: Square, label: "Area" },
  { id: "arrow", icon: ArrowRight, label: "Arrow" },
  { id: "circle", icon: CircleIcon, label: "Circle" },
];

export default function ReviewItem() {
  const { iid } = useParams();
  const nav = useNavigate();
  const { memberships, activeWorkspaceId } = useAuth();
  const myRole = memberships?.find((m) => m.workspace_id === activeWorkspaceId)?.role || "designer";
  const [item, setItem] = useState(null);
  const [activeVersion, setActiveVersion] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [comments, setComments] = useState([]);
  const [tool, setTool] = useState("pin");
  const [pendingAnno, setPendingAnno] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  const [draftInternal, setDraftInternal] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [websiteFullscreen, setWebsiteFullscreen] = useState(false);
  const [rectStart, setRectStart] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [replyInternal, setReplyInternal] = useState(false);

  // Draggable + resizable floating panel (only used when websiteFullscreen is true)
  const panel = useDragResize({
    initial: { x: Math.max(40, (typeof window !== "undefined" ? window.innerWidth : 1280) - 420), y: 80, w: 380, h: 560 },
    minW: 300, minH: 280,
    storageKey: "co_review_item_panel",
  });

  const canvasRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/review-items/${iid}`);
    setItem(data);
    const cur = data.versions.find((v) => v.id === data.current_version_id) || data.versions.slice(-1)[0];
    setActiveVersion(cur);
  };

  useEffect(() => { load(); }, [iid]);

  useEffect(() => {
    if (!activeVersion) return;
    setFileData(null);
    api.get(`/versions/${activeVersion.id}/file`).then((r) => setFileData(r.data));
    api.get(`/versions/${activeVersion.id}/comments`).then((r) => setComments(r.data));
  }, [activeVersion]);

  const refreshComments = async () => {
    const { data } = await api.get(`/versions/${activeVersion.id}/comments`);
    setComments(data);
  };

  // Click on canvas
  const onCanvasClick = (e) => {
    if (activeVersion?.is_locked) return;
    if (drafting || pendingAnno) return;
    if (tool !== "pin") return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingAnno({ type: "pin", x, y });
    setDrafting(true);
    warnTouchPrecision();
  };

  const onMouseDown = (e) => {
    if (activeVersion?.is_locked) return;
    if (!["rect", "arrow", "circle"].includes(tool) || drafting) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setRectStart({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  const onMouseMove = (e) => {
    if (!rectStart) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    if (tool === "arrow") {
      setPendingAnno({ type: "arrow", x: rectStart.x, y: rectStart.y, end_x: x, end_y: y });
    } else if (tool === "circle") {
      setPendingAnno({
        type: "circle",
        x: Math.min(rectStart.x, x),
        y: Math.min(rectStart.y, y),
        w: Math.abs(x - rectStart.x),
        h: Math.abs(y - rectStart.y),
      });
    } else {
      setPendingAnno({
        type: "rect",
        x: Math.min(rectStart.x, x),
        y: Math.min(rectStart.y, y),
        w: Math.abs(x - rectStart.x),
        h: Math.abs(y - rectStart.y),
      });
    }
  };

  const onMouseUp = () => {
    if (rectStart && pendingAnno) {
      const big = pendingAnno.type === "arrow"
        ? (Math.abs((pendingAnno.end_x || 0) - pendingAnno.x) > 1 || Math.abs((pendingAnno.end_y || 0) - pendingAnno.y) > 1)
        : ((pendingAnno.w || 0) > 1 || (pendingAnno.h || 0) > 1);
      if (big) {
        setDrafting(true);
      } else {
        setPendingAnno(null);
      }
    } else {
      setPendingAnno(null);
    }
    setRectStart(null);
  };

  const submitComment = async () => {
    if (!draftBody.trim()) return toast.error("Type something first");
    // Optimistic insert
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      body: draftBody,
      annotation: pendingAnno,
      is_internal: draftInternal,
      is_client: false,
      author_name: "You",
      status: "open",
      created_at: new Date().toISOString(),
      parent_id: null,
      _pending: true,
    };
    setComments((prev) => [...prev, optimistic]);
    const body = draftBody, anno = pendingAnno, internal = draftInternal;
    setDraftBody(""); setPendingAnno(null); setDrafting(false); setDraftInternal(false);
    try {
      const resp = await api.post(`/versions/${activeVersion.id}/comments`, {
        body, annotation: anno, is_internal: internal,
      });
      const real = resp.data;
      // Optimistic-to-real swap WITHOUT a server round-trip first — guarantees the new
      // comment is immediately visible regardless of refresh latency or race conditions.
      setComments((prev) => {
        const withoutTmp = prev.filter((c) => c.id !== tempId);
        // Avoid duplicate if a refresh already inserted it
        if (real?.id && withoutTmp.some((c) => c.id === real.id)) return withoutTmp;
        return [...withoutTmp, real];
      });
      // Best-effort background reconciliation. If this fails (or returns the same data)
      // we already have the right state from the swap above.
      refreshComments().catch(() => {});
      toast.success("Comment added");
      const newId = real?.id;
      if (newId) {
        setSelectedCommentId(newId);
        // Wait a tick so the new node is in the DOM, then scroll panel + canvas to it.
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="comment-${newId}"]`);
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 80);
      }
    } catch (e) {
      setComments((prev) => prev.filter((c) => c.id !== tempId));
      toast.error(apiErrorMessage(e, "Failed to add comment"));
    }
  };

  const submitReply = async (parentId) => {
    if (!replyBody.trim()) return toast.error("Type something first");
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      body: replyBody,
      annotation: null,
      is_internal: replyInternal,
      is_client: false,
      author_name: "You",
      status: "open",
      created_at: new Date().toISOString(),
      parent_id: parentId,
      _pending: true,
    };
    setComments((prev) => [...prev, optimistic]);
    const body = replyBody, internal = replyInternal;
    setReplyBody(""); setReplyingTo(null); setReplyInternal(false);
    try {
      const resp = await api.post(`/versions/${activeVersion.id}/comments`, {
        body, parent_id: parentId, is_internal: internal,
      });
      const real = resp.data;
      setComments((prev) => {
        const withoutTmp = prev.filter((c) => c.id !== tempId);
        if (real?.id && withoutTmp.some((c) => c.id === real.id)) return withoutTmp;
        return [...withoutTmp, real];
      });
      refreshComments().catch(() => {});
    } catch (e) {
      setComments((prev) => prev.filter((c) => c.id !== tempId));
      toast.error(apiErrorMessage(e, "Failed to send reply"));
    }
  };

  const cancelDraft = () => { setDrafting(false); setPendingAnno(null); setDraftBody(""); };

  const toggleResolved = async (c) => {
    const newStatus = c.status === "open" ? "resolved" : "open";
    await api.patch(`/comments/${c.id}`, { status: newStatus });
    refreshComments();
  };

  const convertToTask = async (c) => {
    try {
      await api.post(`/comments/${c.id}/convert-to-task`, {
        title: c.body.slice(0, 60),
        description: c.body,
        priority: "medium",
        review_item_id: item.id,
      });
      toast.success("Task created and added to triage");
      refreshComments();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to convert to task"));
    }
  };

  // Upload new version
  const [newVersionFile, setNewVersionFile] = useState(null);
  const [versionBusy, setVersionBusy] = useState(false);
  const submitNewVersion = async (e) => {
    e.preventDefault();
    if (!newVersionFile) return;
    setVersionBusy(true);
    try {
      const dataUrl = await readFileAsDataURL(newVersionFile);
      const { data } = await api.post(`/review-items/${item.id}/versions`, {
        file_data_url: dataUrl,
        file_size: newVersionFile.size,
        mime_type: newVersionFile.type,
      });
      setUploadOpen(false);
      setNewVersionFile(null);
      toast.success(`Version v${data.version_number} uploaded`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Upload failed"));
    } finally { setVersionBusy(false); }
  };

  if (!item || !activeVersion) {
    return <div className="p-10 text-muted-foreground">Loading review…</div>;
  }

  const project = item.project;
  const isLocked = activeVersion.is_locked;
  const topLevelComments = comments.filter((c) => !c.parent_id);
  const childrenOf = (id) => comments.filter((c) => c.parent_id === id);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <header className="flex items-center justify-between px-4 py-2 border-b border-border bg-card">
        <div className="flex items-center gap-4 min-w-0">
          <Link to={project ? `/app/projects/${project.id}` : "/app/projects"} className="text-muted-foreground hover:text-foreground" data-testid="review-back-link">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="min-w-0">
            <div className="label-eyebrow text-[10px]">{project?.name || "Project"}</div>
            <div className="font-bold text-sm truncate" style={{ fontFamily: "var(--font-heading)" }}>{item.title}</div>
          </div>
          <Select value={activeVersion.id} onValueChange={(vid) => {
            const v = item.versions.find((x) => x.id === vid);
            if (v) setActiveVersion(v);
          }}>
            <SelectTrigger className="w-28 h-8 text-xs" data-testid="version-switcher">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {item.versions.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  v{v.version_number} {v.is_locked ? "🔒" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className={
            item.approval_status === "approved" ? "chip chip-approved" :
            item.approval_status === "changes_requested" ? "chip chip-changes" :
            "chip chip-pending"
          }>
            {item.approval_status === "changes_requested" ? "changes" : item.approval_status}
          </span>
          {typeof navigator !== "undefined" && "serviceWorker" in navigator && navigator.serviceWorker?.controller && (
            <span className="chip chip-pending text-[9px] hidden md:inline-flex" title="Cached for offline viewing." data-testid="offline-cache-badge">offline-ready</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="rounded-none h-8" data-testid="upload-version-btn">
                <Upload className="w-3.5 h-3.5 mr-1.5" /> New version
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Upload new version</DialogTitle></DialogHeader>
              <form onSubmit={submitNewVersion} className="space-y-4">
                <input
                  type="file"
                  accept={item.item_type === "image" ? "image/*" : "application/pdf"}
                  onChange={(e) => setNewVersionFile(e.target.files?.[0] || null)}
                  required
                  className="block w-full text-sm border border-input p-2"
                  data-testid="new-version-file-input"
                />
                <Button type="submit" className="w-full rounded-none" disabled={versionBusy} data-testid="new-version-submit-btn">
                  {versionBusy ? "Uploading…" : "Upload version"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          <ShareDialog open={shareOpen} onOpenChange={setShareOpen} versionId={activeVersion.id} itemId={item.id} />
          <ReviewersDialog itemId={item.id} />
          <ActivityLogDialog itemId={item.id} />
          <RatingWidget itemId={item.id} role={myRole} />
          <AIPanel versionId={activeVersion.id} />
          <CertDownload itemId={item.id} />
        </div>
      </header>

      {/* Canvas + comments */}
      <div className="flex flex-col md:flex-row md:h-[calc(100vh-49px)]">
        {/* Toolbar — horizontal on mobile, vertical on desktop */}
        <div className="md:w-12 md:border-r border-b md:border-b-0 border-border bg-card flex md:flex-col items-center md:py-3 px-2 md:px-0 gap-2 overflow-x-auto md:overflow-x-visible">
          {TOOLS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTool(t.id)}
              title={t.label}
              className={`w-10 h-10 md:w-9 md:h-9 shrink-0 flex items-center justify-center rounded-sm transition ${tool === t.id ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground"}`}
              data-testid={`tool-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
            </button>
          ))}
        </div>

        {/* Media area */}
        <div className="flex-1 overflow-auto bg-muted/40 flex items-start justify-center p-3 md:p-6 min-h-[50vh] md:min-h-0">
          {item.item_type === "pdf" && fileData?.file_data_url ? (
            <PdfCanvas
              fileUrl={fileData.file_data_url}
              comments={comments}
              readOnly={isLocked}
              activeTool={tool}
              onCreateAnnotation={(anno) => { setPendingAnno(anno); setDrafting(true); warnTouchPrecision(); }}
              selectedCommentId={selectedCommentId}
              setSelectedCommentId={setSelectedCommentId}
              pendingAnno={pendingAnno}
              testidPrefix="anno"
            />
          ) : item.item_type === "video" && fileData?.file_data_url ? (
            <VideoCanvas
              fileUrl={fileData.file_data_url}
              comments={comments}
              readOnly={isLocked}
              activeTool={tool}
              onCreateAnnotation={(anno) => { setPendingAnno(anno); setDrafting(true); warnTouchPrecision(); }}
              selectedCommentId={selectedCommentId}
              setSelectedCommentId={setSelectedCommentId}
              pendingAnno={pendingAnno}
              testidPrefix="anno"
              reviewItemId={item.id}
            />
          ) : item.item_type === "website" ? (
            <WebsiteCanvas
              url={fileData?.file_data_url || ""}
              comments={comments}
              readOnly={isLocked}
              activeTool={tool}
              onCreateAnnotation={(anno) => { setPendingAnno(anno); setDrafting(true); warnTouchPrecision(); }}
              selectedCommentId={selectedCommentId}
              setSelectedCommentId={setSelectedCommentId}
              pendingAnno={pendingAnno}
              testidPrefix="anno-website"
              reviewItemId={item.id}
              versionId={activeVersion.id}
              fullscreen={websiteFullscreen}
              onToggleFullscreen={() => setWebsiteFullscreen((f) => !f)}
            />
          ) : (
          <div
            ref={canvasRef}
            className={`relative max-w-full ${isLocked ? "" : "cursor-crosshair"} select-none`}
            style={{ width: "min(100%, 1000px)" }}
            onClick={onCanvasClick}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            data-testid="annotation-canvas"
          >
            {item.item_type === "image" && fileData?.file_data_url && (
              <img src={fileData.file_data_url} alt={item.title} className="w-full h-auto block pointer-events-none" draggable={false} />
            )}

            {/* SVG overlay for arrows + circles */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100" data-testid="anno-svg-layer">
              <defs>
                <marker id="arrowhead-img" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L0,6 L6,3 z" fill="hsl(var(--primary))" />
                </marker>
              </defs>
              {comments.filter((c) => c.annotation?.type === "arrow").map((c) => {
                const a = c.annotation;
                const isSel = selectedCommentId === c.id;
                return (
                  <g key={c.id} style={{ pointerEvents: "auto", cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); setSelectedCommentId(c.id); }} data-testid={`anno-arrow-${c.id}`}>
                    <line x1={a.x} y1={a.y} x2={a.end_x || a.x} y2={a.end_y || a.y}
                      stroke={isSel ? "#fff" : "hsl(var(--primary))"} strokeWidth={isSel ? 0.6 : 0.45}
                      markerEnd="url(#arrowhead-img)" vectorEffect="non-scaling-stroke" />
                  </g>
                );
              })}
              {comments.filter((c) => c.annotation?.type === "circle").map((c) => {
                const a = c.annotation;
                const isSel = selectedCommentId === c.id;
                return (
                  <ellipse key={c.id}
                    cx={a.x + (a.w || 0) / 2} cy={a.y + (a.h || 0) / 2}
                    rx={(a.w || 0) / 2} ry={(a.h || 0) / 2}
                    fill={isSel ? "hsl(var(--primary) / 0.25)" : "hsl(var(--primary) / 0.10)"}
                    stroke={isSel ? "#fff" : "hsl(var(--primary))"} strokeWidth={isSel ? 0.6 : 0.45}
                    vectorEffect="non-scaling-stroke"
                    style={{ pointerEvents: "auto", cursor: "pointer" }}
                    onClick={(e) => { e.stopPropagation(); setSelectedCommentId(c.id); }}
                    data-testid={`anno-circle-${c.id}`} />
                );
              })}
              {pendingAnno?.type === "arrow" && (
                <line x1={pendingAnno.x} y1={pendingAnno.y} x2={pendingAnno.end_x || pendingAnno.x} y2={pendingAnno.end_y || pendingAnno.y}
                  stroke="#22c55e" strokeWidth="0.5" markerEnd="url(#arrowhead-img)" vectorEffect="non-scaling-stroke" />
              )}
              {pendingAnno?.type === "circle" && (
                <ellipse cx={pendingAnno.x + (pendingAnno.w || 0) / 2} cy={pendingAnno.y + (pendingAnno.h || 0) / 2}
                  rx={(pendingAnno.w || 0) / 2} ry={(pendingAnno.h || 0) / 2}
                  fill="rgba(34,197,94,0.15)" stroke="#22c55e" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
              )}
            </svg>

            {/* existing annotations */}
            {comments.map((c, i) => {
              if (!c.annotation) return null;
              const isSel = selectedCommentId === c.id;
              if (c.annotation.type === "pin") {
                return (
                  <div
                    key={c.id}
                    className={`anno-pin ${c.status === "resolved" ? "resolved" : ""}`}
                    style={{ left: `${c.annotation.x}%`, top: `${c.annotation.y}%`, outline: isSel ? "2px solid white" : undefined }}
                    onClick={(e) => { e.stopPropagation(); setSelectedCommentId(c.id); }}
                    data-testid={`anno-pin-${c.id}`}
                  >
                    {i + 1}
                  </div>
                );
              }
              if (c.annotation.type === "rect") {
                return (
                  <div
                    key={c.id}
                    className="absolute border-2 border-primary bg-primary/10 pointer-events-auto cursor-pointer"
                    style={{
                      left: `${c.annotation.x}%`, top: `${c.annotation.y}%`,
                      width: `${c.annotation.w}%`, height: `${c.annotation.h}%`,
                    }}
                    onClick={(e) => { e.stopPropagation(); setSelectedCommentId(c.id); }}
                    data-testid={`anno-rect-${c.id}`}
                  />
                );
              }
              return null;
            })}

            {/* pending annotation (ghost) */}
            {pendingAnno?.type === "pin" && (
              <div className="anno-pin pending" style={{ left: `${pendingAnno.x}%`, top: `${pendingAnno.y}%` }} data-testid="anno-pin-pending">
                +
              </div>
            )}
            {pendingAnno?.type === "rect" && (
              <div
                className="anno-rect pending absolute pointer-events-none"
                style={{ left: `${pendingAnno.x}%`, top: `${pendingAnno.y}%`, width: `${pendingAnno.w}%`, height: `${pendingAnno.h}%` }}
                data-testid="anno-rect-pending"
              />
            )}

            {/* Callout bubble — when a pin/shape is selected, anchor a small comment-bubble near it */}
            {(() => {
              const sel = comments.find((c) => c.id === selectedCommentId);
              if (!sel?.annotation) return null;
              const a = sel.annotation;
              let left = a.x, top = a.y;
              if (a.type === "rect" || a.type === "circle") { left = a.x + (a.w || 0); top = a.y; }
              else if (a.type === "arrow") { left = a.end_x ?? a.x; top = a.end_y ?? a.y; }
              const clampLeft = Math.min(Math.max(left, 0), 70);
              const clampTop = Math.min(Math.max(top, 0), 92);
              return (
                <div
                  className="absolute z-30 bg-card border border-primary shadow-xl p-3 max-w-[280px] text-xs"
                  style={{ left: `${clampLeft}%`, top: `${clampTop}%`, transform: "translate(8px, -10px)", pointerEvents: "auto" }}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`anno-callout-${sel.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium">{sel.author_name}</div>
                    <button onClick={(e) => { e.stopPropagation(); setSelectedCommentId(null); }} className="text-muted-foreground hover:text-foreground" data-testid="anno-callout-close">×</button>
                  </div>
                  <div className="text-[11px] mb-2 whitespace-pre-wrap max-h-32 overflow-auto">{sel.body}</div>
                  <div className="text-[9px] text-muted-foreground">{sel.status === "resolved" ? "resolved" : "open"} · use side panel to reply</div>
                </div>
              );
            })()}
          </div>
          )}
        </div>

        {/* Comments panel */}
        <aside
          className={
            websiteFullscreen
              ? "fixed z-50 bg-card border border-border shadow-2xl flex flex-col rounded-sm"
              : "w-full md:w-96 border-t md:border-t-0 md:border-l border-border bg-card flex flex-col"
          }
          style={websiteFullscreen ? { left: panel.x, top: panel.y, width: panel.w, height: panel.h } : undefined}
          data-testid={websiteFullscreen ? "comments-panel-floating" : "comments-panel"}
        >
          <div
            className="px-4 py-3 border-b border-border flex items-center justify-between"
            onMouseDown={websiteFullscreen ? panel.onDragMouseDown : undefined}
            style={websiteFullscreen ? { cursor: "move", userSelect: "none" } : undefined}
            data-testid="comments-panel-header"
          >
            <div className="flex items-center gap-2">
              {websiteFullscreen && <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />}
              <div className="label-eyebrow">Comments · {topLevelComments.length}</div>
            </div>
            <span className="text-xs text-muted-foreground">{isLocked ? "Version locked" : `Tool: ${tool}`}</span>
          </div>
          <div className="flex-1 overflow-auto">
            {drafting && (
              <div className="p-4 border-b border-border bg-muted/40" data-testid="draft-comment-panel">
                <div className="label-eyebrow mb-2">New comment {pendingAnno?.type ? `· ${pendingAnno.type}` : ""}</div>
                <Textarea
                  rows={3}
                  value={draftBody}
                  onChange={(e) => setDraftBody(e.target.value)}
                  placeholder="Your feedback…"
                  className="mb-2"
                  data-testid="draft-comment-textarea"
                  autoFocus
                />
                <div className="flex items-center justify-between text-xs mb-2">
                  <label className="flex items-center gap-2 text-muted-foreground">
                    <Switch checked={draftInternal} onCheckedChange={setDraftInternal} data-testid="draft-internal-toggle" />
                    {draftInternal ? <span className="flex items-center gap-1"><EyeOff className="w-3 h-3" /> Internal only</span> : <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> Visible to client</span>}
                  </label>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={submitComment} className="rounded-none flex-1" data-testid="draft-comment-submit-btn">
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Send
                  </Button>
                  <Button size="sm" variant="outline" onClick={cancelDraft} className="rounded-none" data-testid="draft-comment-cancel-btn">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
            {topLevelComments.length === 0 && !drafting && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                {isLocked ? "This version is locked." : `Click on the ${item.item_type} to drop a pin or drag to draw an area.`}
              </div>
            )}
            <ul>
              {item.item_type === "website" ? (
                groupByUrl(topLevelComments).map(([groupUrl, list]) => (
                  <li key={groupUrl || "no-url"} data-testid={`comment-group-${groupUrl || "default"}`}>
                    <div className="px-4 py-2 bg-muted/40 border-b border-border text-[10px] uppercase tracking-widest font-mono flex items-center gap-2">
                      <span className="text-muted-foreground">{groupUrl ? shortUrl(groupUrl) : "Unanchored"}</span>
                      <span className="chip text-[9px] ml-auto">{list.length}</span>
                    </div>
                    <ul>
                      {list.map((c) => (
                        <CommentNode
                          key={c.id}
                          comment={c}
                          index={comments.indexOf(c) + 1}
                          childrenList={childrenOf(c.id)}
                          selectedCommentId={selectedCommentId}
                          setSelectedCommentId={setSelectedCommentId}
                          onToggleResolved={toggleResolved}
                          onConvertToTask={convertToTask}
                          replyingTo={replyingTo}
                          setReplyingTo={setReplyingTo}
                          replyBody={replyBody}
                          setReplyBody={setReplyBody}
                          replyInternal={replyInternal}
                          setReplyInternal={setReplyInternal}
                          onSubmitReply={submitReply}
                        />
                      ))}
                    </ul>
                  </li>
                ))
              ) : (
                topLevelComments.map((c) => (
                  <CommentNode
                    key={c.id}
                    comment={c}
                    index={comments.indexOf(c) + 1}
                    childrenList={childrenOf(c.id)}
                    selectedCommentId={selectedCommentId}
                    setSelectedCommentId={setSelectedCommentId}
                    onToggleResolved={toggleResolved}
                    onConvertToTask={convertToTask}
                    replyingTo={replyingTo}
                    setReplyingTo={setReplyingTo}
                    replyBody={replyBody}
                    setReplyBody={setReplyBody}
                    replyInternal={replyInternal}
                    setReplyInternal={setReplyInternal}
                    onSubmitReply={submitReply}
                  />
                ))
              )}
            </ul>
          </div>
          {websiteFullscreen && (
            <div
              className="absolute right-0 bottom-0 w-4 h-4 cursor-se-resize"
              onMouseDown={panel.onResizeMouseDown}
              style={{
                background: "linear-gradient(135deg, transparent 0%, transparent 50%, hsl(var(--muted-foreground) / 0.4) 50%, hsl(var(--muted-foreground) / 0.4) 100%)",
              }}
              data-testid="panel-resize-handle"
              title="Resize"
            />
          )}
        </aside>
      </div>
    </div>
  );
}

function AIPanel({ versionId }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("summary");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [categorize, setCategorize] = useState(null);

  const run = async (kind) => {
    setBusy(true);
    setTab(kind);
    try {
      const { data } = await api.get(`/versions/${versionId}/ai/${kind}`);
      if (kind === "summary") setSummary(data);
      if (kind === "readiness") setReadiness(data);
      if (kind === "categorize") setCategorize(data);
    } catch (e) {
      const msg = apiErrorMessage(e, "AI request failed");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-none h-8" data-testid="ai-panel-trigger-btn">
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" /> AI insights on this version
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-2 border-b border-border pb-3">
          <Button size="sm" variant={tab === "summary" ? "default" : "outline"} onClick={() => run("summary")} disabled={busy} className="rounded-none" data-testid="ai-run-summary-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Summarize
          </Button>
          <Button size="sm" variant={tab === "categorize" ? "default" : "outline"} onClick={() => run("categorize")} disabled={busy} className="rounded-none" data-testid="ai-run-categorize-btn">
            <Tags className="w-3.5 h-3.5 mr-1.5" /> Categorize
          </Button>
          <Button size="sm" variant={tab === "readiness" ? "default" : "outline"} onClick={() => run("readiness")} disabled={busy} className="rounded-none" data-testid="ai-run-readiness-btn">
            <Gauge className="w-3.5 h-3.5 mr-1.5" /> Approval readiness
          </Button>
        </div>
        <div className="min-h-[180px] text-sm" data-testid="ai-result-pane">
          {busy && <div className="text-muted-foreground text-center py-10">Asking the AI…</div>}
          {!busy && tab === "summary" && summary && (
            <div className="space-y-4" data-testid="ai-summary-result">
              <div>
                <div className="label-eyebrow mb-1">Executive summary</div>
                <p>{summary.summary}</p>
              </div>
              {summary.themes?.length > 0 && (
                <div>
                  <div className="label-eyebrow mb-2">Recurring themes</div>
                  <div className="flex flex-wrap gap-2">
                    {summary.themes.map((t, i) => (
                      <span key={i} className="chip chip-pending">{t.label} · {t.count}</span>
                    ))}
                  </div>
                </div>
              )}
              {summary.top_action_items?.length > 0 && (
                <div>
                  <div className="label-eyebrow mb-2">Top action items</div>
                  <ul className="list-disc pl-5 space-y-1">
                    {summary.top_action_items.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          {!busy && tab === "categorize" && categorize && (
            <div className="space-y-4" data-testid="ai-categorize-result">
              <div className="label-eyebrow">By category</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(categorize.by_category || {}).map(([k, v]) => (
                  <span key={k} className="chip chip-pending">{k} · {v}</span>
                ))}
                {Object.keys(categorize.by_category || {}).length === 0 && <span className="text-muted-foreground text-xs">No comments to categorize.</span>}
              </div>
            </div>
          )}
          {!busy && tab === "readiness" && readiness && (
            <div className="space-y-4" data-testid="ai-readiness-result">
              <div className="flex items-end gap-4">
                <div className="text-5xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>{readiness.score}</div>
                <div>
                  <div className="label-eyebrow">{readiness.label}</div>
                  <div className="text-xs text-muted-foreground">Out of 100</div>
                </div>
              </div>
              {readiness.reasons?.length > 0 && (
                <div>
                  <div className="label-eyebrow mb-1">Why</div>
                  <ul className="list-disc pl-5 space-y-1">
                    {readiness.reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
              {readiness.blockers?.length > 0 && (
                <div>
                  <div className="label-eyebrow mb-1 text-red-500">Blockers</div>
                  <ul className="list-disc pl-5 space-y-1 text-red-600">
                    {readiness.blockers.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          {!busy && tab === "summary" && !summary && (
            <p className="text-muted-foreground text-center py-10">Click a button above to run AI on this version&apos;s comments.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CertDownload({ itemId }) {
  const [certNo, setCertNo] = useState(null);

  useEffect(() => {
    api.get(`/review-items/${itemId}/approvals`).then((r) => {
      const approved = (r.data || []).find((a) => a.decision === "approved");
      if (approved) setCertNo(approved.certificate_no);
    }).catch(() => {});
  }, [itemId]);

  if (!certNo) return null;
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/cert/${certNo}.pdf`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid="cert-pdf-download-btn">
      <Button size="sm" variant="outline" className="rounded-none h-8">
        <FileDown className="w-3.5 h-3.5 mr-1.5" /> Certificate PDF
      </Button>
    </a>
  );
}


function fmtVideoTime(s) {
  if (typeof s !== "number" || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}

function shortUrl(u) {
  if (!u || typeof u !== "string") return "";
  try {
    const url = new URL(u);
    const path = url.pathname === "/" ? "" : url.pathname;
    return `${url.hostname}${path}`;
  } catch { return u.length > 28 ? u.slice(0, 28) + "…" : u; }
}

function groupByUrl(commentsList) {
  // Returns [[url, [comments]]] preserving first-occurrence order.
  const map = new Map();
  for (const c of commentsList) {
    const k = c.annotation?.url || "";
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(c);
  }
  return Array.from(map.entries());
}

function CommentNode({
  comment: c, index, childrenList, selectedCommentId, setSelectedCommentId,
  onToggleResolved, onConvertToTask, replyingTo, setReplyingTo, replyBody, setReplyBody,
  replyInternal, setReplyInternal, onSubmitReply,
}) {
  const isReplying = replyingTo === c.id;
  return (
    <li
      onClick={() => setSelectedCommentId(c.id)}
      className={`px-4 py-3 border-b border-border cursor-pointer hover:bg-muted/40 ${selectedCommentId === c.id ? "bg-muted/40" : ""} ${c._pending ? "opacity-60" : ""}`}
      data-testid={`comment-${c.id}`}
    >
      <div className="flex items-center justify-between text-xs mb-1">
        <div className="flex items-center gap-2 flex-wrap">
          {c.annotation && (
            <span className="anno-pin relative w-5 h-5 text-[10px]" style={{ position: "relative", transform: "none", border: "none", boxShadow: "none" }}>{index}</span>
          )}
          <span className="font-medium">{c.author_name}</span>
          {c.annotation?.t != null && (
            <span className="chip text-[9px] font-mono" data-testid={`comment-timestamp-${c.id}`}>
              @ {fmtVideoTime(c.annotation.t)}
            </span>
          )}
          {c.annotation?.page != null && (
            <span className="chip text-[9px]">p.{c.annotation.page}</span>
          )}
          {c.annotation?.url && (
            <span className="chip text-[9px] font-mono" title={c.annotation.url} data-testid={`comment-url-${c.id}`}>
              {shortUrl(c.annotation.url)}
            </span>
          )}
          {c.is_internal && <span className="chip chip-internal text-[9px]">internal</span>}
          {c.is_client && <span className="chip chip-client text-[9px]">client</span>}
        </div>
        <span className={c.status === "resolved" ? "chip chip-resolved" : "chip chip-open"}>{c.status}</span>
      </div>
      <div className="text-sm mb-2 whitespace-pre-wrap">{c.body}</div>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span>{fmtRelative(c.created_at)}</span>
        <div className="flex gap-2">
          {!c._pending && (
            <button onClick={(e) => { e.stopPropagation(); setReplyingTo(isReplying ? null : c.id); }} className="hover:text-primary inline-flex items-center" data-testid={`comment-reply-${c.id}`}>
              <MessageSquare className="w-3 h-3 mr-1" /> Reply
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); onToggleResolved(c); }} className="hover:text-primary" data-testid={`comment-resolve-${c.id}`}>
            {c.status === "resolved" ? "Reopen" : "Resolve"}
          </button>
          {!c.task_id && (
            <button onClick={(e) => { e.stopPropagation(); onConvertToTask(c); }} className="hover:text-primary inline-flex items-center" data-testid={`comment-task-${c.id}`}>
              <ListChecks className="w-3 h-3 mr-1" /> Task
            </button>
          )}
          {c.task_id && <span className="text-primary">Task ✓</span>}
        </div>
      </div>

      {/* Reply composer */}
      {isReplying && (
        <div className="mt-3 p-3 bg-muted/40 border border-border rounded-sm" onClick={(e) => e.stopPropagation()} data-testid={`reply-composer-${c.id}`}>
          <Textarea
            rows={2}
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            placeholder="Reply…"
            className="mb-2"
            data-testid={`reply-textarea-${c.id}`}
            autoFocus
          />
          <div className="flex items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <Switch checked={replyInternal} onCheckedChange={setReplyInternal} data-testid={`reply-internal-toggle-${c.id}`} />
              {replyInternal ? "Internal" : "Visible to client"}
            </label>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setReplyingTo(null); setReplyBody(""); }} className="rounded-none h-7 text-xs" data-testid={`reply-cancel-${c.id}`}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => onSubmitReply(c.id)} className="rounded-none h-7 text-xs" data-testid={`reply-submit-${c.id}`}>
                <Send className="w-3 h-3 mr-1" /> Reply
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Nested children (single level) */}
      {childrenList && childrenList.length > 0 && (
        <ul className="mt-2 pl-3 border-l-2 border-border" data-testid={`comment-children-${c.id}`}>
          {childrenList.map((ch) => (
            <ReplyItem
              key={ch.id}
              comment={ch}
              onToggleResolved={onToggleResolved}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function ReplyItem({ comment: c, onToggleResolved }) {
  return (
    <li
      className={`px-3 py-2 border-b border-border last:border-b-0 ${c._pending ? "opacity-60" : ""}`}
      data-testid={`comment-${c.id}`}
    >
      <div className="flex items-center gap-2 text-xs mb-1 flex-wrap">
        <span className="font-medium">{c.author_name}</span>
        {c.is_internal && <span className="chip chip-internal text-[9px]">internal</span>}
        {c.is_client && <span className="chip chip-client text-[9px]">client</span>}
        <span className="text-[10px] text-muted-foreground ml-auto">{fmtRelative(c.created_at)}</span>
      </div>
      <div className="text-sm whitespace-pre-wrap">{c.body}</div>
    </li>
  );
}
